<template>
  <section id="work" class="w3-row-padding w3-padding-64 w3-theme-l1">
    <div class="container">
      <!-- <h2 class="section-title">{{ $t('services.title') }}</h2> -->
      <h3 class="w3-center">{{ $t('services.title') }}</h3>
      <!-- Carousel Indicators -->
      <div class="carousel-indicators">
        <button v-for="index in totalSlides" :key="index" @click="goToSlide(index - 1)"
          :class="{ active: currentSlide === index - 1 }" class="indicator"></button>
      </div>
      <!-- Carousel Controls -->
      <div class="carousel-controls">
        <button @click="prevSlide" class="carousel-btn prev">
          <!-- :disabled="currentSlide === 0"> -->
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <button @click="nextSlide" class="carousel-btn next">
          <!-- :disabled="currentSlide >= maxSlide"> -->
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- Carousel Container -->
      <div class="carousel-container" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay"
        @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
        <div class="carousel-track" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
          <div v-for="(slide, slideIndex) in slides" :key="slideIndex" class="carousel-slide">
            <div class="services-grid">
              <div v-for="service in slide" :key="service.title" class="service-card">
                <!-- <div class="service-number">{{ getServiceNumber(slideIndex, service) }}</div> -->
                <div class="service-card-title">
                  <h5 style="font-size: 22px;" class="redsh">{{ service.title }}</h5>
                </div>
                <div class="service-card-descr">
                  <div v-for="descr in service.descriptions" :key="descr">
                    <h5>{{ descr }}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Swipe Instruction for Mobile -->
        <div v-if="showSwipeHint" class="swipe-hint">
          <div class="swipe-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M7 14L12 19L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </div>
          <span>Swipe to navigate</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CarouselSection',
  data() {
    return {
      currentSlide: 0,
      servicesPerSlide: 3,
      autoPlayInterval: null,
      isAutoPlayPaused: false,
      touchStartX: 0,
      touchCurrentX: 0,
      isDragging: false,
      touchOffset: 0,
      showSwipeHint: false
    }
  },
  computed: {
    services() {
      const servicesData = this.$tm('services.items');
      return Array.isArray(servicesData) ? servicesData : [];
    },
    slides() {
      const result = [];
      for (let i = 0; i < this.services.length; i += this.servicesPerSlide) {
        result.push(this.services.slice(i, i + this.servicesPerSlide));
      }
      return result;
    },
    totalSlides() {
      return this.slides.length;
    },
    maxSlide() {
      return Math.max(0, this.totalSlides - 1);
    }
  },
  methods: {
    getServiceNumber(slideIndex, service) {
      const index = this.services.findIndex(s => s.title === service.title);
      return (index + 1).toString().padStart(2, '0');
    },

    // Touch event handlers
    handleTouchStart(e) {
      if (this.totalSlides <= 1) return;

      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchCurrentX = touch.clientX;
      this.isDragging = true;
      this.pauseAutoPlay();
    },

    handleTouchMove(e) {
      if (!this.isDragging || this.totalSlides <= 1) return;

      const touch = e.touches[0];
      this.touchCurrentX = touch.clientX;

      const deltaX = this.touchCurrentX - this.touchStartX;
      const containerWidth = e.currentTarget.offsetWidth;

      // Calculate offset with resistance
      this.touchOffset = deltaX * 0.7;

      // Prevent dragging beyond boundaries
      const maxOffset = containerWidth * 0.3;
      if (Math.abs(this.touchOffset) > maxOffset) {
        this.touchOffset = this.touchOffset > 0 ? maxOffset : -maxOffset;
      }
    },

    handleTouchEnd() {
      if (!this.isDragging || this.totalSlides <= 1) return;

      const deltaX = this.touchCurrentX - this.touchStartX;
      const containerWidth = this.$el.querySelector('.carousel-container').offsetWidth;
      const swipeThreshold = containerWidth * 0.2;

      if (Math.abs(deltaX) > swipeThreshold) {
        if (deltaX > 0) {
          // Swipe right - go to previous slide
          this.prevSlide();
        } else {
          // Swipe left - go to next slide
          this.nextSlide();
        }
      }

      // Reset touch state
      this.isDragging = false;
      this.touchOffset = 0;
      this.resumeAutoPlay();
    },

    // Auto-play methods
    startAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
      }

      if (this.totalSlides > 1) {
        this.autoPlayInterval = setInterval(() => {
          if (!this.isAutoPlayPaused && this.totalSlides > 1) {
            if (this.currentSlide < this.maxSlide) {
              this.currentSlide++;
            } else {
              this.currentSlide = 0;
            }
          }
        }, 4000);
      }
    },

    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    },

    pauseAutoPlay() {
      this.isAutoPlayPaused = true;
    },

    resumeAutoPlay() {
      this.isAutoPlayPaused = false;
    },

    // Carousel navigation methods
    nextSlide() {
      if (this.currentSlide < this.maxSlide) {
        this.currentSlide++;
      } else {
        this.currentSlide = 0;
      }
      this.stopAutoPlay();
      this.startAutoPlay();
    },

    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
      } else {
        this.currentSlide = this.maxSlide;
      }
      this.stopAutoPlay();
      this.startAutoPlay();
    },

    goToSlide(index) {
      if (index >= 0 && index < this.totalSlides) {
        this.currentSlide = index;
        this.stopAutoPlay();
        this.startAutoPlay();
      }
    },

    updateServicesPerSlide() {
      const width = window.innerWidth;
      if (width < 768) {
        this.servicesPerSlide = 1;
        this.showSwipeHint = this.totalSlides > 1;
      } else if (width < 1024) {
        this.servicesPerSlide = 2;
        this.showSwipeHint = false;
        } else if (width < 1800){
          this.servicesPerSlide = 3;
          this.showSwipeHint = false;      
        // }else{
        //   this.servicesPerSlide = 6;
        //   this.showSwipeHint = false;
        // }
      } else {
        this.servicesPerSlide = 6;
        this.showSwipeHint = false;
      }
    }
  },
  mounted() {
    this.updateServicesPerSlide();
    window.addEventListener('resize', this.updateServicesPerSlide);

    this.startAutoPlay();

    // Hide swipe hint after 3 seconds
    if (this.showSwipeHint) {
      setTimeout(() => {
        this.showSwipeHint = false;
      }, 3000);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateServicesPerSlide);
    this.stopAutoPlay();
  }
}
</script>

<style scoped>
.section {
  padding: 5rem 2rem;
  background: #ffffff;
  scroll-margin-top: 80px;
}

.section.light {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.container {
  /* max-width: 1200px; */
  margin: 0px auto;
  position: relative;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Carousel Styles */
.carousel-container {
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  border-radius: 3px;

  /* cursor: grab; */

  /* background: #ffffff; */
  /* opacity: 0.1; */
  /* box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08); */
  /* border: 1px solid #e2e8f0; */
}

.carousel-container:active {
  cursor: grabbing;
}

.carousel-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  height: 100%;
  will-change: transform;
}

.carousel-slide {
  flex: 0 0 100%;
  min-width: 100%;
  display: flex;
  align-items: center;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  /* grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); */
  gap: 2rem;
  padding: 2rem;
  width: 100%;
}

/* Swipe Hint */
.swipe-hint {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #64748b;
  backdrop-filter: blur(10px);
  border: 1px solid #e2e8f0;
  animation: fadeInOut 3s ease-in-out;
}

.swipe-icon {
  animation: bounce 2s infinite;
}

.swipe-icon svg {
  width: 16px;
  height: 16px;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }

  20% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  80% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-5px);
  }

  60% {
    transform: translateY(-3px);
  }
}

/* Redesigned Carousel Controls */
.carousel-controls {
  position: absolute;
  top: 50%;
  bottom: 50%;
  left: 0;
  right: 0;
  /* transform: translateY(-50%); */
  display: flex;
  justify-content: space-between;
  /* justify-content: center; */
  padding: 0 2rem;
  z-index: 1;
  pointer-events: none;
}

.carousel-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 0px solid #667eea;
  color: #667eea;
  /* width: 56px; */
  height: 56px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  /* box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2); */
  pointer-events: all;
  position: relative;
  overflow: hidden;
}

.prev {
  left: 0.5%;
}

.next {
  right: 0.5%;
}

.carousel-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  /* box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3); */
  /* color: #ffffff; */
  border: 1px solid #667eea;
}

/* .carousel-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.carousel-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  color: #ffffff;
}

.carousel-btn:hover:not(:disabled)::before {
  opacity: 1;
}

.carousel-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.95);
}

.carousel-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  background: #f8fafc;
  border-color: #cbd5e0;
  color: #cbd5e0;
}

.carousel-btn svg {
  transition: transform 0.2s ease;
  position: relative;
  z-index: 1;
}

.carousel-btn:hover:not(:disabled) svg {
  transform: scale(1.1);
} */

/* Carousel Indicators */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2.5rem;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: none;
  background: #cbd5e0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.indicator::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: left 0.3s ease;
}

.indicator.active::before {
  left: 0;
}

.indicator.active {
  transform: scale(1.2);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  border-radius: 2px;
}

.indicator:hover:not(.active) {
  background: #667eea;
  transform: scale(1.1);
}

/* Service Card Styles - Light Mode & Squared */
.service-card {
  /* opacity: 1; */
  background: #ffffff;
  padding: 2.5rem 2rem;
  border-radius: 3px;
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  /* flex-direction: column;
  justify-content: flex-start; */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  /* flex like in the about */
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: space-evenly;
  align-items: center;
  
}

.service-card-descr {
  /* opacity: 1; */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  /* text-align: center;  
  justify-content: center; */
}

.service-card-title {
  
  /* display:inline ; */
  display:table-cell;
  vertical-align: middle;
}


.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.service-card:hover {
  /* transform: translateY(-8px); */
  border-color: #667eea;
  /* background-color: #e7eaf5; */
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.15);
}

.service-card:hover::before {
  transform: scaleX(1);
}

.service-number {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
  transition: transform 0.3s ease;
}

.service-card:hover .service-number {
  transform: scale(1.1);
}

.service-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2d3748;
  font-weight: 600;
}

.service-card p {
  color: #718096;
  line-height: 1.6;
  font-size: 0.95rem;
}

/* Hide indicators when only one slide */
.carousel-indicators:only-child,
.carousel-indicators:first-child:last-child {
  display: none;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .services-grid {
    grid-template-columns: 1fr;
    padding: 1.5rem;
    gap: 1.5rem;
  }

  .section {
    padding: 3rem 1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .carousel-controls {
    padding: 0 0.5rem;
  }

  .carousel-btn {
    width: 48px;
    height: 48px;
  }

  .carousel-btn svg {
    width: 20px;
    height: 20px;
  }

  .service-card {
    padding: 2rem 1.5rem;
  }

  .service-number {
    font-size: 2.5rem;
  }

  /* Hide buttons on mobile when swipe is available */
  .carousel-controls {
    display: none;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 1.5rem 1rem;
  }

  .carousel-btn {
    width: 42px;
    height: 42px;
  }

  .carousel-btn svg {
    width: 18px;
    height: 18px;
  }

  .service-number {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .service-card h3 {
    font-size: 1.3rem;
  }

  .service-card p {
    font-size: 0.9rem;
  }
}

/* Additional Light Mode Enhancements */
.carousel-container:hover {
  /* box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12); */
}

.service-card:active {
  transform: translateY(-2px);
}

/* Focus states for accessibility */
.carousel-btn:focus-visible {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

.indicator:focus-visible {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

.service-card:focus-within {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

/* Smooth transitions for touch interactions */
.carousel-track {
  transition: transform 0.3s ease;
}

.carousel-container.dragging .carousel-track {
  transition: none;
}
</style>